import { useOrgStore } from "@/store/org";
import { useUserStore } from "@/store/user";
import { USER_APPLICATIONS } from "~/constants/userApplications";

export type AuthGrant = {
  application?: string;
};

export const AUTH_GRANTS_CLAIM_KEY = "https://onclusive.com/grants";

const REQUIRED_AUTH_GRANT_APPLICATIONS = [
  USER_APPLICATIONS.UNIFIED_APPLICATION,
  "pro" as const,
] as const;

const getStoredObject = <T extends object>(key: string): T => {
  const storedValue = localStorage.getItem(key);
  if (!storedValue) return {} as T;

  try {
    const parsedValue: unknown = JSON.parse(storedValue);
    return parsedValue &&
      typeof parsedValue === "object" &&
      !Array.isArray(parsedValue)
      ? (parsedValue as T)
      : ({} as T);
  } catch {
    return {} as T;
  }
};

export function setUserOrgFromLocalStorage() {
  const userStore = useUserStore();
  userStore.setUser(getStoredObject("user"));

  const orgStore = useOrgStore();
  orgStore.setOrg(getStoredObject("organization"));
}

export const extractEmailFromError = (
  errorDescription: string | undefined,
): string => {
  const emailMatch = errorDescription?.match(/for\s+([\w.+-]+@[\w.-]+)/);
  return (emailMatch?.[1] || "").replace(/\.$/, "");
};

export const saveEmailToLocalStorage = (email: string): void => {
  if (email) {
    localStorage.setItem("logged_in_user_email", email);
  }
};

export const getAuthGrantsFromClaims = (
  claims: Record<string, unknown> | undefined,
): AuthGrant[] => {
  return Array.isArray(claims?.[AUTH_GRANTS_CLAIM_KEY])
    ? (claims[AUTH_GRANTS_CLAIM_KEY] as AuthGrant[])
    : [];
};

export const hasRequiredAuthGrants = (authGrants: AuthGrant[]): boolean => {
  const applications = new Set(
    authGrants
      .map((grant) => grant.application)
      .filter((application): application is string => Boolean(application)),
  );

  return REQUIRED_AUTH_GRANT_APPLICATIONS.every((requiredApplication) =>
    applications.has(requiredApplication),
  );
};
